// client/src/pages/ChatRoom.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { io } from "socket.io-client";

const SOCKET_URL = process.env.REACT_APP_API_URL;

/**
 * Fixed ChatRoom:
 * - waits for auth to resolve (user === undefined -> show loading)
 * - if user is null -> navigate to login
 * - if server returns 403/404 when loading history -> show "chat unavailable" message (no redirect)
 * - joins correct socket rooms (join_group / join_room)
 * - safe dedupe of messages
 * - keeps delivery/seen updates
 */

export default function ChatRoom() {
  const { type, postId, participantId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  const isGroup = participantId === "group";

  const pushMessage = (msg) => {
    if (!msg) return;
    setMessages((prev) => {
      if (msg._id && prev.some((m) => String(m._id) === String(msg._id))) return prev;
      if (!msg._id) {
        const key = `${String(msg.text || "").trim()}::${String(msg.timestamp || msg.createdAt || "")}`;
        const found = prev.some((m) => `${String(m.text || "").trim()}::${String(m.timestamp || m.createdAt || "")}` === key);
        if (found) return prev;
      }
      return [...prev, msg];
    });
  };

  useEffect(() => {
    // wait until auth resolved
    if (user === undefined) return; // still loading
    if (!user) {
      // user explicitly not logged in
      navigate("/login");
      return;
    }

    // validate params
    if (!type || !postId || !participantId) {
      console.error("Invalid chat params:", { type, postId, participantId });
      setUnavailable(true);
      setLoading(false);
      return;
    }

    let mounted = true;
    setUnavailable(false);

    const loadHistory = async () => {
      try {
        const token = localStorage.getItem("cp_token");
        const res = await fetch(`${SOCKET_URL}/api/chat/${type}/${postId}/${participantId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) {
          // If forbidden / not found -> show friendly UI instead of redirecting to login/home
          console.warn("Chat history load failed", data);
          if (res.status === 403 || res.status === 404) {
            setUnavailable(true);
            return;
          }
          // other server errors - show unavailable as well
          setUnavailable(true);
          return;
        }
        if (!mounted) return;
        setMessages(Array.isArray(data.messages) ? data.messages : []);
      } catch (err) {
        console.error("Load error:", err);
        setUnavailable(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadHistory();

    // socket setup & join rooms
    socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });

    socketRef.current.on("connect", () => {
      if (user?.id) socketRef.current.emit("auth", { userId: user.id });

      if (isGroup) {
        socketRef.current.emit("join_group", { type, postId, userId: user.id });
      } else {
        socketRef.current.emit("join_room", { type, postId, participantId });
      }
    });

    socketRef.current.on("new_message", ({ message }) => {
      if (!message) return;
      pushMessage(message);
    });

    socketRef.current.on("message_delivered", ({ messageIds }) => {
      if (!Array.isArray(messageIds)) return;
      setMessages((prev) => prev.map((m) => (messageIds.map(String).includes(String(m._id)) ? { ...m, delivered: true, deliveredAt: new Date().toISOString() } : m)));
    });

    socketRef.current.on("message_seen", ({ messageIds }) => {
      if (!Array.isArray(messageIds)) return;
      setMessages((prev) => prev.map((m) => (messageIds.map(String).includes(String(m._id)) ? { ...m, seen: true, seenAt: new Date().toISOString() } : m)));
    });

    socketRef.current.on("announcement", ({ text: annText, sender, timestamp }) => {
      const ann = { _id: `ann_${Date.now()}`, sender, text: `📣 ${annText}`, timestamp: timestamp || new Date().toISOString() };
      pushMessage(ann);
    });

    return () => {
      mounted = false;
      socketRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, postId, participantId, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const token = localStorage.getItem("cp_token");
      if (isGroup) {
        const res = await fetch(`${SOCKET_URL}/api/chat/${type}/${postId}/group`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text }),
        });
        const d = await res.json();
        if (!res.ok) {
          return alert(d.error || "Failed to send");
        }
        const msg = d.message;
        if (msg) pushMessage(msg);
        setText("");
        socketRef.current.emit("send_message", { type, postId, message: msg, isGroup: true });
      } else {
        const res = await fetch(`${SOCKET_URL}/api/chat/${type}/${postId}/${participantId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text }),
        });
        const d = await res.json();
        if (!res.ok) {
          return alert(d.error || "Failed to send");
        }
        const msg = d.message;
        if (msg) pushMessage(msg);
        setText("");
        socketRef.current.emit("send_message", { type, postId, participantId, message: msg });
      }
    } catch (err) {
      console.error("Send error:", err);
    } finally {
      setSending(false);
    }
  };

  if (user === undefined || loading) return <div className="p-6 text-gray-700 dark:text-gray-200">Loading chat…</div>;
  if (unavailable) return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white dark:bg-slate-800 p-6 rounded shadow">
        <h2 className="text-xl font-semibold mb-4">Chat unavailable</h2>
        <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          The chat for this post is not available — maybe the post was removed or you don't have permission to view it.
        </div>
        <button onClick={() => navigate("/home")} className="px-4 py-2 bg-blue-600 text-white rounded">Back to Home</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-white dark:bg-slate-800 p-4 rounded shadow">
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-semibold">{isGroup ? "Group Chat" : "Chat"}</h2>
          <button onClick={() => navigate("/home")} className="px-3 py-1 border rounded">Back</button>
        </div>

        <div className="h-96 overflow-y-auto border p-3 rounded bg-slate-50 dark:bg-slate-900">
          {messages.map((m) => {
            const isMe = String(m.sender) === String(user?.id);
            const key = m._id || `${m.text || ""}_${m.timestamp || m.createdAt || ""}`;
            return (
              <div key={key} className={`mb-3 flex ${isMe ? "justify-end" : "justify-start"}`}>
                <div className={`px-3 py-2 rounded max-w-[70%] ${isMe ? "bg-blue-600 text-white" : "bg-gray-300 dark:bg-gray-700 dark:text-white"}`}>
                  {m.text}
                  <div className="text-xs opacity-60 text-right mt-1">
                    {m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : (m.createdAt ? new Date(m.createdAt).toLocaleTimeString() : "")}
                    {isMe && (" " + (m.seen ? "✓✓ (seen)" : m.delivered ? "✓✓ (delivered)" : "✓ (sent)"))}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className="flex gap-2 mt-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            className="flex-1 p-2 border rounded dark:bg-slate-700 dark:text-white"
            placeholder={isGroup ? "Message group…" : "Type…"}
            disabled={sending}
          />
          <button onClick={sendMessage} disabled={sending} className="px-4 py-2 bg-blue-600 text-white rounded">
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
